
import { useEffect } from 'react';
import { useProductSelection } from '../useProductSelection';
import { useProductOperations } from './useProductOperations';
import { useProductFetching } from './useProductFetching';
import { formatDate } from './utils';
import { Product } from '@/lib/supabase/products';

interface UseProductsDataProps {
  handlePageChange?: (pageId: string) => void;
  shopId?: string;
}

export const useProductsData = ({ handlePageChange, shopId }: UseProductsDataProps = {}) => {
  // Utiliser directement le shopId pour le filtrage
  const {
    products,
    filteredProducts,
    loading,
    fetchProducts,
    organizationId
  } = useProductFetching({ shopId });
  
  // Importer les fonctionnalités de sélection de produits
  const {
    selectedProducts,
    editingProductId,
    addingProduct,
    setAddingProduct,
    toggleProductSelection,
    toggleSelectAll,
    areAllSelected,
    handleEditProduct,
    setEditingProductId,
    handleAddProductForm,
    handleCloseForm
  } = useProductSelection();

  // Importer les opérations sur les produits (ajouter, supprimer, etc.)
  const {
    handleAddProduct: operationsAddProduct,
    handleDeleteProduct,
    handleBulkDeleteProducts,
    shopId: selectedShopId
  } = useProductOperations({
    organizationId,
    products,
    filteredProducts,
    setEditingProductId,
    setAddingProduct,
    fetchProducts,
    shopId
  });

  // Gérer lorsque le bouton ajouter un produit est cliqué
  const handleAddProduct = () => {
    console.log("handleAddProduct called in useProductsData with shopId:", shopId);
    
    // D'abord, définir l'ajout à true pour afficher le formulaire
    setAddingProduct(true);
    
    // Ensuite, si nous devons naviguer vers une page de formulaire
    if (handlePageChange) {
      console.log("Navigating to product form page with 'new' ID and shopId:", shopId);
      // Passer l'ID de boutique comme paramètre de requête pour le conserver lors de la navigation
      if (shopId) {
        handlePageChange(`new?shopId=${shopId}`);
      } else {
        handlePageChange('new');
      }
    } else {
      // Sinon, déclencher simplement la fonction operationsAddProduct
      console.log("No page change handler, just setting form state");
      operationsAddProduct();
    }
  };

  // Réinitialiser la sélection des produits lors du changement de boutique
  useEffect(() => {
    console.log("Shop ID changed, resetting product selection");
    // Fix: toggleSelectAll expects a Product[] array, but we were passing a boolean
    // Call it with an empty array when we want to clear all selections
    toggleSelectAll([]);
  }, [shopId]);

  // Retourner toutes les données et fonctions nécessaires
  return {
    products: filteredProducts, // N'exposer que les produits filtrés
    loading,
    selectedProducts,
    editingProductId,
    addingProduct,
    formatDate,
    handleAddProduct,
    handleEditProduct,
    handleDeleteProduct,
    handleBulkDeleteProducts,
    fetchProducts,
    toggleProductSelection,
    toggleSelectAll,
    areAllSelected,
    setEditingProductId,
    setAddingProduct,
    handleAddProductForm,
    handleCloseForm,
    filterProductsByShops: fetchProducts,
    shopId
  };
};

// Exporter formatDate pour la compatibilité ascendante
export { formatDate } from './utils';
